import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Search, BookOpen, Copy, Check, Loader2, AlertCircle, FileText, BookMarked, FolderOpen, File, ChevronRight, ChevronDown, Type, AlignLeft, Hash, Eye, EyeOff, FileSearch } from 'lucide-react'

interface RawFileEntry {
  category: string
  count: number
  files: string[]
}

interface ContentSearchResult {
  file: string
  match_count: number
  matches: { line: number; context: string }[]
}

interface ContentSearchResponse {
  category: string
  query: string
  results: ContentSearchResult[]
  total_files_searched: number
}

const HF_SPACE = 'https://riddwam-al-munazir-ai.hf.space'

const RAW_CATEGORIES = [
  { id: 'christianity', label: 'Christianity', icon: BookMarked },
  { id: 'islam', label: 'Islam', icon: BookMarked },
  { id: 'judaism', label: 'Judaism', icon: BookMarked },
  { id: 'hinduism', label: 'Hinduism', icon: BookMarked },
] as const

type SearchMode = 'browse' | 'search'

export function KnowledgeBase() {
  const [rawCategory, setRawCategory] = useState('')
  const [rawFiles, setRawFiles] = useState<string[] | null>(null)
  const [rawLoading, setRawLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string | null>(null)
  const [fileContentLoading, setFileContentLoading] = useState(false)
  const [fileSearch, setFileSearch] = useState('')
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [serviceStatus, setServiceStatus] = useState<'ok' | 'loading' | 'error'>('ok')
  const [showLineNumbers, setShowLineNumbers] = useState(true)
  const [searchMode, setSearchMode] = useState<SearchMode>('browse')
  const [contentQuery, setContentQuery] = useState('')
  const [contentResults, setContentResults] = useState<ContentSearchResult[] | null>(null)
  const [contentSearchLoading, setContentSearchLoading] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    fetch(`${HF_SPACE}/health`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'ok' && data.build_progress === null) {
          setServiceStatus('ok')
        } else if (data.build_progress) {
          setServiceStatus('loading')
          setError(`Knowledge base is still loading (${data.build_progress}). Please wait...`)
        }
      })
      .catch(() => {
        setServiceStatus('error')
        setError('Cannot connect to the knowledge base server.')
      })
  }, [])

  const copyText = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleRawCategoryChange = useCallback(async (cat: string) => {
    setRawCategory(cat)
    setSelectedFile(null)
    setFileContent(null)
    setFileSearch('')
    setContentQuery('')
    setContentResults(null)
    setExpandedDirs(new Set())
    if (!cat) { setRawFiles(null); return }

    setRawLoading(true)
    setError(null)
    try {
      const resp = await fetch(`${HF_SPACE}/raw/${encodeURIComponent(cat)}`)
      if (!resp.ok) throw new Error(`Failed to list files: ${resp.status}`)
      const data: RawFileEntry = await resp.json()
      setRawFiles(data.files)
      setExpandedDirs(new Set(data.files.map(f => f.split('/')[0]).filter(Boolean)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file list')
      setRawFiles(null)
    } finally {
      setRawLoading(false)
    }
  }, [])

  const handleFileClick = useCallback(async (filePath: string) => {
    if (selectedFile === filePath) { setSelectedFile(null); setFileContent(null); return }
    setSelectedFile(filePath)
    setFileContentLoading(true)
    setError(null)
    try {
      const encodedPath = filePath.split('/').map(p => encodeURIComponent(p)).join('/')
      const resp = await fetch(`${HF_SPACE}/raw/${encodeURIComponent(rawCategory)}/${encodedPath}`)
      if (!resp.ok) throw new Error(`Failed to fetch file: ${resp.status}`)
      const data = await resp.json()
      setFileContent(data.text)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file content')
      setFileContent(null)
    } finally {
      setFileContentLoading(false)
    }
  }, [rawCategory, selectedFile])

  const handleContentSearch = useCallback(async (query: string) => {
    setContentQuery(query)
    if (!query.trim() || !rawCategory) {
      setContentResults(null)
      return
    }
    setContentSearchLoading(true)
    setError(null)
    try {
      const resp = await fetch(`${HF_SPACE}/raw/${encodeURIComponent(rawCategory)}/search?q=${encodeURIComponent(query)}&max_files=50`)
      if (!resp.ok) throw new Error(`Search failed: ${resp.status}`)
      const data: ContentSearchResponse = await resp.json()
      setContentResults(data.results)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
      setContentResults(null)
    } finally {
      setContentSearchLoading(false)
    }
  }, [rawCategory])

  const toggleDir = (dir: string) => {
    setExpandedDirs(prev => {
      const next = new Set(prev)
      if (next.has(dir)) next.delete(dir)
      else next.add(dir)
      return next
    })
  }

  const getDirGroups = (files: string[]): Map<string, string[]> => {
    const groups = new Map<string, string[]>()
    for (const f of files) {
      const parts = f.split('/')
      const dir = parts.length > 1 ? parts[0] : '(root)'
      if (!groups.has(dir)) groups.set(dir, [])
      groups.get(dir)!.push(f)
    }
    return groups
  }

  const filteredFiles = rawFiles
    ? fileSearch
      ? rawFiles.filter(f => f.toLowerCase().includes(fileSearch.toLowerCase()))
      : rawFiles
    : []

  const dirGroups = getDirGroups(filteredFiles)

  const fileMetadata = useMemo(() => {
    if (!fileContent) return null
    const lines = fileContent.split('\n')
    const words = fileContent.trim() ? fileContent.trim().split(/\s+/).length : 0
    const chars = fileContent.length
    return { lines: lines.length, words, chars }
  }, [fileContent])

  const contentLines = useMemo(() => {
    if (!fileContent) return []
    return fileContent.split('\n')
  }, [fileContent])

  if (serviceStatus === 'loading') {
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[hsl(var(--accent))] mx-auto mb-4" />
          <p className="text-[hsl(var(--fg))] font-medium">{error || 'Loading knowledge base...'}</p>
          <p className="text-small text-[hsl(var(--fg-muted))] mt-2">The server is building its index. This may take a few minutes.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6">
          <h1 className="text-h2 text-[hsl(var(--fg))] mb-1">Raw Texts</h1>
          <p className="text-small text-[hsl(var(--fg-muted))]">
            Browse full scripture files across religious traditions.
          </p>
        </div>

        {error && serviceStatus === 'ok' && (
          <div className="card mb-6 border-[hsl(var(--error)/0.3)] bg-[hsl(var(--error-bg))]">
            <div className="flex items-center gap-2 p-4 text-small text-[hsl(var(--error))]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left panel */}
          <div className="space-y-4">
            <div className="card">
              <div className="p-4 space-y-4">
                <div className="flex flex-wrap gap-2">
                  {RAW_CATEGORIES.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => handleRawCategoryChange(id)}
                      className={`btn ${rawCategory === id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                    >
                      <Icon className="h-3.5 w-3.5 mr-1.5" />
                      {label}
                    </button>
                  ))}
                </div>

                {rawCategory && (
                  <div className="tab-bar">
                    <button
                      onClick={() => setSearchMode('browse')}
                      className={`tab-item ${searchMode === 'browse' ? 'active' : ''}`}
                    >
                      <FolderOpen className="h-3.5 w-3.5 inline-block mr-1 -mt-0.5" />
                      Browse
                    </button>
                    <button
                      onClick={() => setSearchMode('search')}
                      className={`tab-item ${searchMode === 'search' ? 'active' : ''}`}
                    >
                      <FileSearch className="h-3.5 w-3.5 inline-block mr-1 -mt-0.5" />
                      Search
                    </button>
                  </div>
                )}

                {rawCategory && searchMode === 'browse' && rawFiles && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />
                    <input
                      type="text"
                      value={fileSearch}
                      onChange={e => setFileSearch(e.target.value)}
                      placeholder="Filter by filename..."
                      className="input-field pl-9 h-9"
                    />
                  </div>
                )}

                {rawCategory && searchMode === 'search' && (
                  <div className="relative">
                    <FileSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />
                    <input
                      type="text"
                      value={contentQuery}
                      onChange={e => {
                        setContentQuery(e.target.value)
                        clearTimeout(searchTimeout.current)
                        searchTimeout.current = setTimeout(() => handleContentSearch(e.target.value), 300)
                      }}
                      placeholder="Search inside file contents..."
                      className="input-field pl-9 h-9"
                    />
                  </div>
                )}
              </div>
            </div>

            {rawLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" />
              </div>
            )}

            {!rawLoading && rawCategory && !rawFiles && !error && (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <BookMarked className="h-8 w-8 text-[hsl(var(--fg-muted))] mb-3 opacity-40" />
                  <p className="text-small text-[hsl(var(--fg-muted))]">Select a category to browse files</p>
                </div>
              </div>
            )}

            {/* Browse mode: file tree */}
            {!rawLoading && searchMode === 'browse' && filteredFiles.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <p className="text-caption font-medium text-[hsl(var(--fg-muted))] uppercase tracking-wider">
                    {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-caption text-[hsl(var(--fg-muted))]">
                    {dirGroups.size} director{dirGroups.size !== 1 ? 'ies' : 'y'}
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {Array.from(dirGroups.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([dir, files]) => (
                    <div key={dir}>
                      <button
                        onClick={() => toggleDir(dir)}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-caption font-medium text-[hsl(var(--fg))] hover:bg-[hsl(var(--accent))]/30 transition-all border-b border-[hsl(var(--border))]/50"
                      >
                        {expandedDirs.has(dir) ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                        <FolderOpen className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" />
                        <span className="truncate">{dir}</span>
                        <span className="ml-auto text-caption text-[hsl(var(--fg-muted))] shrink-0">{files.length}</span>
                      </button>
                      {expandedDirs.has(dir) && files.sort().map(f => (
                        <button
                          key={f}
                          onClick={() => handleFileClick(f)}
                          className={`flex items-center gap-2 w-full pl-12 pr-4 py-2 text-caption transition-all border-b border-[hsl(var(--border))]/30 ${
                            selectedFile === f
                              ? 'bg-[hsl(var(--accent-subtle))] text-[hsl(var(--accent))]'
                              : 'text-[hsl(var(--fg-muted))] hover:bg-[hsl(var(--accent))]/20 hover:text-[hsl(var(--fg))]'
                          }`}
                          title={f}
                        >
                          <File className="h-3 w-3 shrink-0" />
                          <span className="truncate">{f.split('/').pop()}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Browse mode: no results */}
            {!rawLoading && searchMode === 'browse' && rawCategory && rawFiles && filteredFiles.length === 0 && (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="h-8 w-8 text-[hsl(var(--fg-muted))] mb-3 opacity-40" />
                  <p className="text-small text-[hsl(var(--fg-muted))]">
                    {fileSearch ? `No files matching "${fileSearch}"` : 'No files found'}
                  </p>
                </div>
              </div>
            )}

            {/* Search mode: results */}
            {!rawLoading && searchMode === 'search' && contentSearchLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))] mb-2" />
                <p className="text-caption text-[hsl(var(--fg-muted))] ml-2">Searching file contents...</p>
              </div>
            )}

            {!rawLoading && searchMode === 'search' && !contentSearchLoading && contentQuery && contentResults && contentResults.length === 0 && (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileSearch className="h-8 w-8 text-[hsl(var(--fg-muted))] mb-3 opacity-40" />
                  <p className="text-small text-[hsl(var(--fg-muted))]">
                    No matches for "{contentQuery}"
                  </p>
                </div>
              </div>
            )}

            {!rawLoading && searchMode === 'search' && !contentSearchLoading && contentResults && contentResults.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <p className="text-caption font-medium text-[hsl(var(--fg-muted))] uppercase tracking-wider">
                    {contentResults.reduce((s, r) => s + r.match_count, 0)} matches in {contentResults.length} files
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {contentResults.map(result => (
                    <div key={result.file} className="border-b border-[hsl(var(--border))]/50 last:border-0">
                      <button
                        onClick={() => { handleFileClick(result.file); setSearchMode('browse') }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-caption font-medium text-[hsl(var(--fg))] hover:bg-[hsl(var(--accent))]/30 transition-all border-b border-[hsl(var(--border))]/30"
                      >
                        <FileText className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" />
                        <span className="truncate">{result.file}</span>
                        <span className="ml-auto text-caption text-[hsl(var(--accent))] shrink-0">{result.match_count}</span>
                      </button>
                      {result.matches.map((m, i) => (
                        <div key={i} className="px-4 py-2 pl-10">
                          <pre className="text-caption text-[hsl(var(--fg-muted))] leading-relaxed whitespace-pre-wrap font-mono border-l-2 border-[hsl(var(--accent)/0.3)] pl-3">
                            {m.context}
                          </pre>
                          <p className="text-[9px] text-[hsl(var(--fg-muted))] mt-1 opacity-60">Line {m.line}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!rawLoading && searchMode === 'search' && contentQuery && !contentSearchLoading && contentResults === null && !error && (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FileSearch className="h-6 w-6 text-[hsl(var(--fg-muted))] mb-2 opacity-40" />
                  <p className="text-caption text-[hsl(var(--fg-muted))]">Press Enter or wait to search</p>
                </div>
              </div>
            )}
          </div>

          {/* Right panel: file content */}
          <div className="lg:border-l lg:border-[hsl(var(--border))] lg:pl-6 min-h-[300px]">
            {!selectedFile && !fileContentLoading && (
              <div className="flex flex-col items-center justify-center py-20 text-center h-full">
                <div className="rounded-full bg-[hsl(var(--elevated))] p-4 mb-4">
                  <FileText className="h-8 w-8 text-[hsl(var(--fg-muted))] opacity-60" />
                </div>
                <p className="text-small font-medium text-[hsl(var(--fg))] mb-1">No file selected</p>
                <p className="text-caption text-[hsl(var(--fg-muted))] max-w-[200px]">
                  Choose a category and select a file from the left panel to view its contents.
                </p>
              </div>
            )}

            {fileContentLoading && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))] mb-3" />
                <p className="text-caption text-[hsl(var(--fg-muted))]">Loading file content...</p>
              </div>
            )}

            {selectedFile && !fileContentLoading && fileContent !== null && (
              <div className="animate-fade-in space-y-3">
                <div className="card">
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-[hsl(var(--accent))] shrink-0" />
                          <p className="text-small font-medium text-[hsl(var(--fg))] truncate">
                            {selectedFile.split('/').pop()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-caption text-[hsl(var(--fg-muted))] capitalize bg-[hsl(var(--elevated))] px-2 py-0.5 rounded-full">
                            {rawCategory}
                          </span>
                          <span className="text-caption text-[hsl(var(--fg-muted))]">
                            {selectedFile}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setShowLineNumbers(!showLineNumbers)}
                          className="btn btn-ghost btn-icon"
                          title={showLineNumbers ? 'Hide line numbers' : 'Show line numbers'}
                        >
                          {showLineNumbers ? <Hash className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => copyText(fileContent, selectedFile)}
                          className="btn btn-ghost btn-icon"
                          title="Copy text"
                        >
                          {copiedId === selectedFile ? <Check className="h-4 w-4 text-[hsl(var(--success))]" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {fileMetadata && (
                      <div className="flex items-center gap-4 pb-3 border-b border-[hsl(var(--border))] mb-3">
                        <div className="flex items-center gap-1.5 text-caption text-[hsl(var(--fg-muted))]">
                          <AlignLeft className="h-3 w-3" />
                          {fileMetadata.lines} lines
                        </div>
                        <div className="flex items-center gap-1.5 text-caption text-[hsl(var(--fg-muted))]">
                          <Type className="h-3 w-3" />
                          {fileMetadata.words.toLocaleString()} words
                        </div>
                        <div className="flex items-center gap-1.5 text-caption text-[hsl(var(--fg-muted))]">
                          <Hash className="h-3 w-3" />
                          {fileMetadata.chars.toLocaleString()} chars
                        </div>
                      </div>
                    )}

                    <div
                      ref={contentRef}
                      className="rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))] overflow-hidden"
                    >
                      <div className="max-h-[65vh] overflow-y-auto">
                        <table className="w-full border-collapse">
                          <tbody>
                            {contentLines.map((line, i) => (
                              <tr key={i} className="hover:bg-[hsl(var(--accent))]/20 transition-colors">
                                {showLineNumbers && (
                                  <td className="select-none text-right text-[11px] text-[hsl(var(--fg-muted))] px-3 py-[1px] border-r border-[hsl(var(--border))] align-top w-[1%] whitespace-nowrap">
                                    {i + 1}
                                  </td>
                                )}
                                <td className="text-small text-[hsl(var(--fg)/0.9)] leading-relaxed whitespace-pre-wrap font-mono px-3 py-[1px]">
                                  {line || <span className="select-none">&nbsp;</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedFile && !fileContentLoading && fileContent === null && !error && (
              <div className="card border-[hsl(var(--error)/0.3)]">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-6 w-6 text-[hsl(var(--error))] mb-3" />
                  <p className="text-small text-[hsl(var(--fg))]">Failed to load file</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
